<template>
  <v-container fluid v-if="trigger">
    <validation-observer ref="form">
      <v-form @submit.prevent="onSubmit">
        <v-card outlined>
          <v-container fluid id="global_settings">
            <v-card outlined>
              <v-toolbar flat>
                <v-card-subtitle>{{ $t('trigger.globalSettings') }}</v-card-subtitle>
              </v-toolbar>
              <v-container fluid>
                <v-form>
                  <v-row>
                    <v-col cols="12" lg="6" xl="6">
                      <v-text-field v-model="trigger.name" dense hide-details="auto" :label="$t('trigger.name')" />
                    </v-col>
                    <v-col cols="12" lg="6" xl="6">
                      <validation-provider class="width-100" rules="required" name="event" v-slot="{ errors }">
                        <v-autocomplete
                          dense
                          :error-messages="errors"
                          v-model="trigger.event"
                          :items="events"
                          :label="$t('trigger.event')"
                          hide-details="auto"
                        />
                      </validation-provider>
                    </v-col>
                    <v-col cols="12">
                      <v-switch dense v-model="trigger.active" :label="$t('trigger.active')" class="mt-0"></v-switch>
                    </v-col>
                  </v-row>
                </v-form>
              </v-container>
            </v-card>
          </v-container>
          <v-container fluid id="conditions">
            <condition-builder
              v-model="trigger.conditions"
              :fields="fields"
              :label="$t('trigger.conditions')"
            />
          </v-container>
          <v-container fluid id="calls">
            <v-card outlined :class="callsBlocIsEmpty ? 'card-error' : ''">
              <v-toolbar flat>
                <v-card-subtitle>{{ $t('trigger.calls') }}</v-card-subtitle>
                <v-spacer />
                <v-btn
                  fab
                  outlined
                  small
                  color="rgb(100, 100, 100, 0.2)"
                  @click="dragAndDropEnabled = !dragAndDropEnabled"
                  class="mr-1"
                >
                  <v-icon :color="dragAndDropEnabled ? 'green' : 'primary'">mdi-cursor-move</v-icon>
                </v-btn>
                <v-btn
                  fab
                  outlined
                  small
                  color="rgb(100, 100, 100, 0.2)"
                  @click="trigger.calls.push({ type: 'arn', call_parameters: {} })"
                >
                  <v-icon color="primary">mdi-plus</v-icon>
                </v-btn>
              </v-toolbar>
              <v-container fluid v-if="!callsBlocIsEmpty">
                <draggable tag="v-col" class="ma-0 pa-0" v-model="trigger.calls" :disabled="!dragAndDropEnabled">
                  <v-card outlined v-for="(call, index) in trigger.calls" v-bind:key="index" class="mb-2">
                    <v-toolbar flat>
                      <v-text-field
                        outlined
                        class="pr-8"
                        v-model="call.name"
                        dense
                        hide-details="auto"
                        :label="$t('trigger.name')"
                        :disabled="dragAndDropEnabled"
                        :error-messages="errors"
                      />
                      <v-btn color="primary" outlined @click="trigger.calls.splice(index, 1)">
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </v-toolbar>
                    <v-container fluid>
                      <v-row>
                        <v-col cols="12" lg="6" xl="6">
                          <validation-provider
                            class="width-100"
                            rules="required"
                            name="trigger call type"
                            v-slot="{ errors }"
                          >
                            <v-autocomplete
                              v-model="call.type"
                              :items="callTypes"
                              dense
                              hide-details="auto"
                              :label="$t('trigger.callType')"
                              :disabled="dragAndDropEnabled"
                              :error-messages="errors"
                            />
                          </validation-provider>
                        </v-col>
                        <v-col cols="12" lg="6" xl="6">
                          <validation-provider
                            class="width-100"
                            rules="required"
                            name="trigger url"
                            v-slot="{ errors }"
                          >
                            <v-text-field
                              v-model="call.call_parameters.url"
                              dense
                              hide-details="auto"
                              :label="$t('trigger.url')"
                              :disabled="dragAndDropEnabled"
                              :error-messages="errors"
                            />
                          </validation-provider>
                        </v-col>
                        <v-col cols="12" lg="6" xl="6" v-if="call.type === 'arn'">
                          <v-autocomplete
                            v-model="call.call_parameters.invocation_type"
                            :items="invocationTypes"
                            dense
                            hide-details="auto"
                            :label="$t('trigger.invocationType')"
                            :disabled="dragAndDropEnabled"
                          />
                        </v-col>
                        <v-col cols="12" lg="6" xl="6" v-if="call.type === 'arn'">
                          <v-autocomplete
                            v-model="call.call_parameters.log_type"
                            :items="logTypes"
                            dense
                            hide-details="auto"
                            :label="$t('trigger.logType')"
                            :disabled="dragAndDropEnabled"
                          />
                        </v-col>
                        <v-col cols="12" lg="6" xl="6" v-if="call.type === 'http'">
                          <v-text-field
                            v-model="call.call_parameters.headers"
                            dense
                            hide-details="auto"
                            :label="$t('trigger.headers')"
                            :disabled="dragAndDropEnabled"
                          />
                        </v-col>
                        <v-col cols="12" lg="6" xl="6" v-if="call.type === 'http'">
                          <v-text-field
                            v-model="call.call_parameters.body"
                            dense
                            hide-details="auto"
                            :label="$t('trigger.body')"
                            :disabled="dragAndDropEnabled"
                          />
                        </v-col>
                        <v-col cols="12" lg="6" xl="6" v-if="call.type === 'http'">
                          <v-text-field
                            v-model="call.call_parameters.http_method"
                            dense
                            hide-details="auto"
                            :label="$t('trigger.httpMethod')"
                            :disabled="dragAndDropEnabled"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </draggable>
              </v-container>
            </v-card>
          </v-container>
          <v-toolbar flat justify-content="center" height="auto" class="mb-2">
            <v-row justify="center">
              <v-btn class="ma-2" color="primary" outlined :loading="loading" :disabled="loading" type="submit">
                {{ $t('trigger.save') }}
              </v-btn>
              <v-btn
                class="ma-2"
                color="primary"
                outlined
                :loading="loading"
                :disabled="loading"
                @click="deleteTriggerDialog = true"
                v-if="!isTriggerCreation"
              >
                {{ $t('trigger.delete') }}
              </v-btn>
            </v-row>
          </v-toolbar>
        </v-card>
      </v-form>
    </validation-observer>
    <confirmation-delete-dialog
      v-model="deleteTriggerDialog"
      :title="$t('trigger.deleteTrigger')"
      :text="$t('trigger.deleteTriggerText')"
      :loading="loading"
      @yes="deleteTrigger"
      @no="deleteTriggerDialog = false"
    />
  </v-container>
</template>
<script>
  import { EventBus } from '../../event-bus'
  import { mapState, mapActions } from 'vuex'
  import draggable from 'vuedraggable'
  import confirmationDialog from '../../components/ConfirmationDialog.vue'
  import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
  import { required } from 'vee-validate/dist/rules'
  import ConditionBuilder from '@/components/ConditionBuilder.vue'

  extend('required', {
    ...required,
    message: 'This field is required',
  })

  export default {
    name: 'TriggerDetail',
    components: {
      ValidationObserver: ValidationObserver,
      ValidationProvider: ValidationProvider,
      draggable: draggable,
      confirmationDeleteDialog: confirmationDialog,
      ConditionBuilder,
    },
    data() {
      return {
        loading: false,
        trigger: undefined,
        dragAndDropEnabled: false,
        deleteTriggerDialog: false,
        fields: ['current_status', 'type'],
        callTypes: [
          'arn',
          // "http"
        ],
        invocationTypes: ['Event', 'RequestResponse', 'DryRun'],
        logTypes: ['None', 'Tail'],
      }
    },
    mounted() {
      if (this.$route.params.id && this.$route.params.id != 'add') {
        this.get()
      } else {
        this.trigger = {
          conditions: [],
          active: true,
          name: undefined,
          event: undefined,
          calls: [],
        }
      }
    },
    methods: {
      ...mapActions(['updateBreadcrumb']),
      onSubmit: function () {
        if (!this.checkEmptyConditions() && !this.checkEmptyCalls()) {
          this.$refs.form.validate().then((success) => {
            if (success) {
              if (this.isTriggerCreation) {
                this.create()
              } else {
                this.update()
              }
            }
          })
        }
      },
      create() {
        this.loading = true
        let triggerCopy = JSON.parse(JSON.stringify(this.trigger))

        let path = '/v1/int/triggers/'
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: triggerCopy,
        }
        this.$Amplify.API.post('triggers', path, myInit)
          .then((res) => {
            this.trigger = res

            let breadCrumbsIndex = this.breadcrumbs.findIndex((item) => item.to === this.$route.path)
            this.updateBreadcrumb({
              index: breadCrumbsIndex,
              value: {
                text: this.$t('breadcrumbs.TriggersDetail', { name: this.trigger.name }),
                to: '/triggers/' + this.trigger._id,
              },
            })

            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('trigger.triggerCreated'),
              color: 'success',
            })
            this.loading = false
            this.$router.push(`/triggers/${res._id}`)
          })
          .catch((e) => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('trigger.errorTriggerCreate'),
              color: 'error',
            })
            console.log(e)
            this.loading = false
          })
      },
      update() {
        this.loading = true
        let triggerCopy = JSON.parse(JSON.stringify(this.trigger))

        let path = '/v1/int/triggers/' + this.$route.params.id
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: triggerCopy,
        }
        this.$Amplify.API.put('triggers', path, myInit)
          .then((res) => {
            this.trigger = res
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('trigger.triggerUpdated'),
              color: 'success',
            })
            this.loading = false
          })
          .catch((e) => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('trigger.errorTriggerUpdate'),
              color: 'error',
            })
            console.log(e)
            this.loading = false
          })
      },
      get: function () {
        this.loading = true
        let path = '/v1/int/triggers/' + this.$route.params.id
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {},
        }
        this.$Amplify.API.get('triggers', path, myInit)
          .then((res) => {
            this.trigger = res
            let breadCrumbsIndex = this.breadcrumbs.findIndex((item) => item.to === this.$route.path)

            this.updateBreadcrumb({
              index: breadCrumbsIndex,
              value: { text: this.$t('breadcrumbs.TriggersDetail', { name: this.trigger.name }) },
            })
            this.loading = false
          })
          .catch((e) => {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('trigger.errorTriggerLoading'),
              color: 'error',
            })
            this.loading = false
          })
      },
      deleteTrigger() {
        this.loading = true
        let path = '/v1/int/triggers/' + this.$route.params.id
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.del('triggers', path, myInit)
          .then(() => {
            this.loading = false
            this.$router.push('/triggers')
          })
          .catch((e) => {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('trigger.errorTriggerDelete'),
              color: 'error',
            })
            this.loading = false
          })
      },
      checkEmptyConditions() {
        for (const conditionBlock of this.trigger.conditions) {
          if (!conditionBlock.length) {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('trigger.emptyCondition'),
              color: 'error',
            })
            return true
          } else
            for (const condition of conditionBlock) {
              if (['contains_elements_with', 'does_not_contain_elements_with'].includes(condition.operator) && !condition.values_to_check.length) {
                EventBus.$emit('displaySnackbar', {
                  display: true,
                  text: this.$t('trigger.emptySubCondition'),
                  color: 'error',
                })
                return true
              }
            }
        }
        return false
      },
      checkEmptyCalls() {
        if (this.callsBlocIsEmpty) {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('trigger.emptyCalls'),
            color: 'error',
          })
          return true
        } else {
          return false
        }
      },
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService', 'breadcrumbs']),
      events() {
        return [
          { text: this.$t('trigger.createOrder'), value: 'createOrder' },
          { text: this.$t('trigger.createSubOrder'), value: 'createSubOrder' },
          { text: this.$t('trigger.updateOrderStatus'), value: 'updateOrderStatus' },
          { text: this.$t('trigger.updateSubOrderStatus'), value: 'updateSubOrderStatus' },
          { text: this.$t('trigger.updateOrder'), value: 'updateOrder' },
          { text: this.$t('trigger.updateSubOrder'), value: 'updateSubOrder' },
          { text: this.$t('trigger.createSlipOrder'), value: 'createSlipOrder' },
          { text: this.$t('trigger.createReturn'), value: 'createReturn' },
          { text: this.$t('trigger.updateReturnStatus'), value: 'updateReturnStatus' },
          { text: this.$t('trigger.getTransaction'), value: 'getTransaction' },
          { text: this.$t('trigger.listTransactions'), value: 'listTransactions' },
          { text: this.$t('trigger.createReturnLabel'), value: 'createReturnLabel' },
          { text: this.$t('trigger.updateReturn'), value: 'updateReturn' },
          { text: this.$t('trigger.createRefund'), value: 'createRefund' },
          { text: this.$t('trigger.updateRefund'), value: 'updateRefund' },
          { text: this.$t('trigger.createStockTransfer'), value: 'createStockTransfer' },
          { text: this.$t('trigger.updateStockTransfer'), value: 'updateStockTransfer' },
          { text: this.$t('trigger.resendPickupCode'), value: 'resendPickupCode' },
          { text: this.$t('trigger.createLocation'), value: 'createLocation' },
          { text: this.$t('trigger.updateLocation'), value: 'updateLocation' },
          { text: this.$t('trigger.deleteLocation'), value: 'deleteLocation' },
          { text: this.$t('trigger.createStockParsing'), value: 'createStockParsing' },
          { text: this.$t('trigger.updateStockParsing'), value: 'updateStockParsing' },
        ]
      },
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      isTriggerCreation() {
        return this.trigger._id === undefined
      },
      callsBlocIsEmpty() {
        return !this.trigger.calls || this.trigger.calls.length === 0
      },
    },
    watch: {
      // redirect to trigger list if we go back from details to add
      '$route.params.id'() {
        if (!this.$route.params.id && this.trigger._id) {
          this.$router.push(`/triggers/`)
        }
      },
    },
  }
</script>

<style scoped>
  .v-btn__content {
    width: 100%;
    white-space: normal;
  }
</style>
